/* eslint-disable react/prop-types */
import { Input } from "antd";
import React, {
	useState,
	useEffect,
	useRef,
	memo,
	forwardRef,
	useImperativeHandle,
} from "react";
const KEY_BACKSPACE = "Backspace";
const KEY_DELETE = "Delete";
const KEY_F2 = "F2";

const NumericEditor = memo(
	forwardRef((props: any, ref) => {
		const createInitialState = () => {
			let startValue;
			let highlightAllOnFocus = true;

			if (
				props.eventKey === KEY_BACKSPACE ||
				props.eventKey === KEY_DELETE
			) {
				startValue = "";
			} else if (props.charPress) {
				startValue = props.charPress;
				highlightAllOnFocus = false;
			} else {
				startValue = props.value;
				if (props.eventKey === KEY_F2) {
					highlightAllOnFocus = false;
				}
			}

			return {
				value: startValue,
				highlightAllOnFocus,
			};
		};

		const initialState = createInitialState();
		const [value, setValue] = useState(initialState.value);
		const [highlightAllOnFocus, setHighlightAllOnFocus] = useState(
			initialState.highlightAllOnFocus
		);
		const refInput = useRef<any>(null);

		// focus on the input
		useEffect(() => {
			// get ref from React component
			const eInput = refInput.current;
			eInput.focus();
			if (highlightAllOnFocus) {
				eInput.select();
			} else {
				// when we started editing, we want the caret at the end, not the start.
				// this comes into play in two scenarios:
				//   a) when user hits F2
				//   b) when user hits a printable character
				const length = eInput.value ? eInput.value.length : 0;
				if (length > 0) {
					eInput.setSelectionRange(length, length);
				}
			}
		}, []);

		/* Utility Methods */
		const cancelBeforeStart =
			props.charPress && "1234567890".indexOf(props.charPress) < 0;

		const onChange = (event: any) => {
			const inputValue = event.target.value;
			if (!inputValue) {
				setValue(inputValue);
				return;
			}
			let regex = /^([+-]|([+-]?[0-9]+))$/;
			if (!props.disableDecimals) {
				regex = /^([+-]|[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+))$/;
			}
			if (regex.test(inputValue)) {
				setValue(inputValue);
			}
		};

		/* Component Editor Lifecycle methods */
		useImperativeHandle(ref, () => {
			return {
				// the final value to send to the grid, on completion of editing
				getValue() {
					if (isNaN(value)) {
						return 0;
					}
					const numberValue = Number(value);
					if (
						numberValue < Number.MIN_SAFE_INTEGER ||
						numberValue > Number.MAX_SAFE_INTEGER
					) {
						return 0;
					}
					return numberValue;
				},

				// Gets called once before editing starts, to give editor a chance to
				// cancel the editing before it even starts.
				isCancelBeforeStart() {
					return cancelBeforeStart;
				},

				// Gets called once when editing is finished (eg if Enter is pressed).
				// If you return true, then the result of the edit will be ignored.
				isCancelAfterEnd() {
					// will reject the number if it greater than 1,000,000
					// not very practical, but demonstrates the method.
					return false;
				},
			};
		});

		return (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					padding: 6,
					height: "100%",
				}}
			>
				<Input
					ref={refInput}
					value={value}
					onChange={onChange}
					style={{
						width: "100%",
						height: "100%",
						paddingLeft: 12,
						fontWeight: 600,
					}}
					inputMode={"decimal"}
					suffix={props.suffix}
				/>
			</div>
		);
	})
);

export default NumericEditor;
